/**
 * A SÍNTESE da Comparação (Análise de Cenários › Comparação), agora um RESUMO da mesma
 * análise do relatório comparativo (lote de 09/10/2026, item 7). Puro: devolve marcação em
 * texto, sem DOM — o simulador legado a escreve em `#p-sintese` pela ponte
 * `window.__fincastAnalise`.
 *
 * O QUE ELA ERA: "Síntese comparativa (IA)", "gerado localmente", um emoji de alvo — e não
 * era IA: quatro regras de "melhor em" (maior PJ contado como vantagem, o que o próprio
 * Guiado desaconselha), empate dado ao primeiro da lista, quatro dos sete critérios
 * ignorados e um "Estoura o LGC em R$ 0" quando o saldo era −0,40. Duas análises diferentes
 * sobre os mesmos números criariam a dúvida de qual está certa: agora é UMA.
 *
 * O OBJETIVO desta síntese é fixo e DECLARADO na própria tela: "Máxima folga / robustez"
 * (a régua mais neutra — não premia gastar nem economizar). Para outro objetivo, o
 * relatório comparativo pergunta na hora de gerar.
 *
 * Os números da tela da Comparação são os do simulador AGORA (o cenário aberto com o que
 * está digitado), e é sobre eles que esta síntese fala — o relatório, que circula, usa só o
 * publicado.
 */
import type { Empreendimento, Lev } from '../../../../engine/index';
import { analisar, fraseDoMaisApertado, fraseDosCriterios } from './analise';
import { textoDaFrase } from './frase';
import type { Frase } from './frase';
import { modeloDoCenario } from './modelo';
import type { ModeloCenario } from './modelo';
import { objetivoPorChave } from './textos';
import type { ChaveObjetivo } from './textos';

export interface ItemSintese {
  id: string;
  nome: string;
  /** o empreendimento como a Comparação o tem (a derivação da tabela é feita aqui, numa cópia) */
  b: Empreendimento;
  lev: Lev;
}

export const OBJETIVO_DA_SINTESE: ChaveObjetivo = 'folga';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const html = (f: Frase): string => esc(textoDaFrase(f));

/* ícone de alvo — SVG inline em currentColor, no lugar do emoji que havia */
const ICONE_ALVO = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" '
  + 'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/>'
  + '<circle cx="12" cy="12" r="1.2"/></svg>';

function selo(c: ModeloCenario): string {
  const tom = !c.suficiente ? 'out' : c.cor === 'green' ? 'ok' : c.cor === 'yellow' ? 'warn' : 'bad';
  return `<span class="badge ${tom}">${esc(c.veredito)}</span>`;
}

export function sinteseComparacao(itens: readonly ItemSintese[]): string {
  if (itens.length < 2) {
    /* o mesmo texto da tela (simulador.js decide este caso antes de chamar a ponte) */
    return '<div class="card"><div class="ch"><h3>Síntese</h3></div><div class="sepln"></div><div class="cc">'
      + '<div class="empty" style="padding:14px;text-align:center;color:hsl(var(--muted-foreground))">'
      + 'Salve 2+ parametrizações para o diagnóstico comparativo.</div></div></div>';
  }
  const objetivo = objetivoPorChave(OBJETIVO_DA_SINTESE)!;
  const cenarios = itens.map((x) => modeloDoCenario({
    id: x.id, nome: x.nome, versaoPublicada: 0, empresaId: '', empreendimentoId: '',
    entrada: { empreendimento: x.b, lev: x.lev }, temRascunhoMeu: false,
  }));
  const a = analisar({ incorporadora: '', empreendimento: '', geradoPor: '', geradoEm: '', objetivo, cenarios });
  const riscosDe = new Map(a.riscos.map((r) => [r.id, r.frases]));

  const cartoes = cenarios.map((c) => {
    const itensLista: string[] = [`<li>${html(fraseDosCriterios(c))}</li>`];
    if (c.suficiente) {
      itensLista.push(`<li>${html(fraseDoMaisApertado(c))}</li>`);
      for (const r of riscosDe.get(c.id) ?? []) itensLista.push(`<li class="con">${html(r)}</li>`);
    }
    return `<div class="acard" data-cenario-id="${esc(c.id)}"><h4>${esc(c.nome)} ${selo(c)}</h4><ul>${itensLista.join('')}</ul></div>`;
  }).join('');

  const rec = a.porque.map((f) => `<li>${html(f)}</li>`).join('');
  const ordem = a.ranking.length > 1
    ? `<li>Ordem para este objetivo: ${a.ranking.map((p) => esc(p.nome)).join(' › ')}.</li>`
    : '';
  const troca = a.tradeoffs.slice(0, 3).map((f) => `<li>${html(f)}</li>`).join('');

  return `<div class="card"><div class="ch"><div><h3>Síntese comparativa</h3>`
    + `<div class="d">Análise por regras — a mesma do relatório comparativo. Objetivo: <b>${esc(objetivo.t)}</b>.</div></div></div>`
    + `<div class="sepln"></div><div class="cc">${cartoes}`
    + `<div class="acard sint-rec"><h4><span class="sint-ic">${ICONE_ALVO}</span>Recomendação</h4><ul>${rec}${ordem}</ul>`
    + (troca ? `<h4 class="sint-sub">O que se troca entre eles</h4><ul>${troca}</ul>` : '')
    + `<p class="sint-pe">Para outro objetivo, gere o relatório comparativo — ele pergunta o objetivo e usa a versão publicada de cada cenário.</p>`
    + `</div></div></div>`;
}
